/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable no-nested-ternary */
/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable jsdoc/require-jsdoc */
import React, { useState, useEffect, useMemo, useRef, useCallback, ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import readXlsxFile from 'read-excel-file';
import moment from 'moment';
import Select from 'react-select';
import { FileSpreadsheet, Upload, ChevronLeft, Trash2, Eye, Loader2, Download, Calendar } from 'lucide-react';
// eslint-disable-next-line import/no-extraneous-dependencies
import * as XLSX from 'xlsx';
import { getResolutionOptions } from '../_helpers/timeResolution';

import { useData } from '../context/data.context';
import PlanGate from '../components/PlanGate';
import MultiSelectDropdown from '../controls/MultiSelectDropDown';
import CustomDropdown from '../controls/CustomDropdown';
import DateRangePicker from '../controls/DateRangePicker';
import HistogramWidget from '../components/histogram/HistogramWidget';
import Deque from '../classes/deque';
import { getDeviceSummaryOverTime } from '../services/unified-dashboard-service';
import {
  getDeviceTimeRangeMetrics,
  uploadS3File,
  createExternalReport,
  getAllExternalReports,
  getExternalReportByKey,
  updateExternalReport,
  deleteExternalReport,
  downloadS3File,
  ExternalReport
} from '../services/dashboard-service';
import { changeZeroValuesToNull, getDisplayableValue } from '../_helpers/globalHelper';
import { getPriorityColor, getclasses } from '../_helpers/dashboardHelper';
import {
  ParametersType,
  TimeSpan,
  DeviceType,
  // ParameterAttribute
  ClientThresholdSettingsAttribute,
  UserConfigV2
} from '../constants/telosair-interfaces';
import Loader from '../components/loader';
import { useUnifiedData } from '../hooks/useUnifiedData';
import { Filters, getAppliedFilter } from '../services/localstorage.service';

type SetupFormData = {
  devices: { label: string; value: string }[];
  startTimeColumn: string;
  endTimeColumn: string;
  timeOffset: string;
};

type ParsedRow = {
  [key: string]: any;
  originalIndex: number;
};

const TIME_OFFSETS = [
  { label: '15 Minutes', value: '15' },
  { label: '30 Minutes', value: '30' },
  { label: '45 Minutes', value: '45' },
  { label: '1 Hour', value: '60' },
  { label: '2 Hours', value: '120' },
  { label: '3 Hours', value: '180' }
];

const ROW_COLORS = [
  'rgba(239, 68, 68, 0.1)', // red-500
  'rgba(249, 115, 22, 0.1)', // orange-500
  'rgba(234, 179, 8, 0.1)', // yellow-500
  'rgba(34, 197, 94, 0.1)', // green-500
  'rgba(6, 182, 212, 0.1)', // cyan-500
  'rgba(59, 130, 246, 0.1)', // blue-500
  'rgba(168, 85, 247, 0.1)', // purple-500
  'rgba(236, 72, 153, 0.1)' // pink-500
];

const ROW_BORDER_COLORS = [
  'rgba(239, 68, 68, 0.8)',
  'rgba(249, 115, 22, 0.8)',
  'rgba(234, 179, 8, 0.8)',
  'rgba(34, 197, 94, 0.8)',
  'rgba(6, 182, 212, 0.8)',
  'rgba(59, 130, 246, 0.8)',
  'rgba(168, 85, 247, 0.8)',
  'rgba(236, 72, 153, 0.8)'
];

type ReportTableRowProps = {
  row: ParsedRow;
  index: number;
  headers: string[];
  selectedTableRows: Set<number>;
  toggleRow: (index: number) => void;
  rowBorderColors: string[];
  startTimeColumn: string;
  endTimeColumn: string;
  devices: { label: string; value: number }[];
  timeOffset: string;
  selectedParameters: ParametersType[];
  // parameters: ParameterAttribute[];
  clientThresholdSettings: ClientThresholdSettingsAttribute | null;
  userConfigDataV2: UserConfigV2 | null;
  deviceType: DeviceType;
};

function ReportTableRow({
  row,
  index,
  headers,
  selectedTableRows,
  toggleRow,
  rowBorderColors,
  startTimeColumn,
  endTimeColumn,
  devices,
  timeOffset,
  selectedParameters, // parameters
  clientThresholdSettings,
  userConfigDataV2,
  deviceType
}: ReportTableRowProps): ReactElement {
  const [metrics, setMetrics] = useState<{
    beforeStartTime: Record<number, any>;
    afterEndTime: Record<number, any>;
    duringTime: Record<number, any>;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const fetchFailedRef = React.useRef(false);
  const loadingRef = React.useRef(false);
  const rowRef = React.useRef<HTMLTableRowElement>(null);

  // Reset metrics when devices/offset change
  useEffect(() => {
    setMetrics(null);
    fetchFailedRef.current = false;
  }, [devices, timeOffset]);

  const fetchMetrics = async (): Promise<void> => {
    if (loadingRef.current || fetchFailedRef.current) return;
    try {
      setLoading(true);
      loadingRef.current = true;
      const start = moment(row[startTimeColumn]).valueOf();
      const end = moment(row[endTimeColumn]).valueOf();

      // Validate timestamps before calling API
      if (Number.isNaN(start) || Number.isNaN(end) || !startTimeColumn || !endTimeColumn) {
        fetchFailedRef.current = true;
        return;
      }

      // Resolve device IDs
      let deviceIds: number[] = [];
      if (devices.some((d) => d.value === 0)) {
        deviceIds = [];
      } else {
        deviceIds = devices.map((d) => d.value);
      }

      const payload = {
        devices: deviceIds,
        startTime: start,
        endTime: end,
        timeDifference: parseInt(timeOffset, 10)
      };

      const response = await getDeviceTimeRangeMetrics(payload);
      if (response.data) {
        setMetrics(response.data);
      }
    } catch (error) {
      console.error('Error fetching row metrics', error);
      fetchFailedRef.current = true;
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !metrics && !loadingRef.current && !fetchFailedRef.current) {
          fetchMetrics();
        }
      },
      { threshold: 0.1 }
    );

    if (rowRef.current) {
      observer.observe(rowRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [metrics, devices, timeOffset, row, startTimeColumn, endTimeColumn]);

  const getMetricValue = (
    data: Record<string, any> | undefined,
    param: ParametersType
  ): { value: string; colorClass: string } => {
    if (!data) return { value: '-', colorClass: 'text-gray-500' };

    const deviceKeys = Object.keys(data);
    if (deviceKeys.length === 0) return { value: '-', colorClass: 'text-gray-500' };

    let sum = 0;
    let count = 0;

    deviceKeys.forEach((key) => {
      const deviceEntry = data[key];
      if (deviceEntry?.parameters) {
        const val = deviceEntry.parameters[param];
        if (typeof val === 'number') {
          sum += val;
          count += 1;
        }
      }
    });

    if (count === 0) return { value: '-', colorClass: 'text-gray-500' };

    const avg = sum / count;
    const colorClass = getclasses(
      true,
      getPriorityColor(param, avg, clientThresholdSettings, userConfigDataV2, deviceType)
    );

    return { value: getDisplayableValue(param, avg), colorClass };
  };

  const getParameterSubValues = (param: ParametersType): { before: { value: string; colorClass: string }; during: { value: string; colorClass: string }; after: { value: string; colorClass: string }; isLoading: boolean } => {
    if (loading) {
      const loadingVal = { value: '...', colorClass: 'text-gray-400 animate-pulse' };
      return { before: loadingVal, during: loadingVal, after: loadingVal, isLoading: true };
    }
    return {
      before: getMetricValue(metrics?.beforeStartTime, param),
      during: getMetricValue(metrics?.duringTime, param),
      after: getMetricValue(metrics?.afterEndTime, param),
      isLoading: false
    };
  };

  return (
    <tr
      ref={rowRef}
      className="group hover:bg-container-500 transition-colors bg-container-900 border-b border-gray-700"
    >
      <td className="p-4 sticky left-0 z-20 transition-colors shadow-[2px_0_5px_-2px_rgba(0,0,0,0.3)] bg-container-900 group-hover:bg-container-500">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={selectedTableRows.has(index)}
            onChange={() => toggleRow(index)}
            className="rounded border-gray-600 bg-container-500 text-primary-600 focus:ring-primary-500"
          />
          <div
            className="w-3 h-3 rounded-full border border-white/10"
            style={{
              backgroundColor: selectedTableRows.has(index)
                ? rowBorderColors[index % rowBorderColors.length]
                : 'transparent'
            }}
          />
        </div>
      </td>
      {headers.map((h) => (
        <React.Fragment key={`${index}-${h}`}>
          <td className="px-4 py-3 whitespace-nowrap transition-colors bg-container-900 group-hover:bg-container-500">
            {String(row[h])}
          </td>
          {h === endTimeColumn && (
            <>
              <td className="px-4 py-3 whitespace-nowrap transition-colors bg-container-900 group-hover:bg-container-500">
                {timeOffset} mins
              </td>
              {selectedParameters.map((param) => {
                const vals = getParameterSubValues(param);
                return (
                  <React.Fragment key={`combined-${param}-${index}`}>
                    <td className="px-3 py-3 text-center align-top transition-colors bg-container-900 group-hover:bg-container-500">
                      <span className={vals.before.colorClass}>{vals.before.value}</span>
                    </td>
                    <td className="px-3 py-3 text-center align-top transition-colors bg-container-900 group-hover:bg-container-500">
                      <span className={vals.during.colorClass}>{vals.during.value}</span>
                    </td>
                    <td className="px-3 py-3 text-center align-top transition-colors bg-container-900 group-hover:bg-container-500">
                      <span className={vals.after.colorClass}>{vals.after.value}</span>
                    </td>
                  </React.Fragment>
                );
              })}
            </>
          )}
        </React.Fragment>
      ))}
    </tr>
  );
}

export default function ReportsPage(): ReactElement {
  const { devices, clientThresholdSettings, userConfigDataV2 } = useData();
  const { parameters } = useUnifiedData();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // State for the flow
  const [step, setStep] = useState<'list' | 'upload' | 'setup' | 'visualize'>('list');
  const [reports, setReports] = useState<ExternalReport[]>([]);
  const [loadingReports, setLoadingReports] = useState(false);

  // Upload/Setup state
  const [excelData, setExcelData] = useState<{ headers: string[]; rows: any[][] } | null>(null);
  const methods = useForm<SetupFormData>({
    defaultValues: {
      devices: [{ label: 'All', value: '0' }],
      timeOffset: TIME_OFFSETS[0].value
    }
  });
  const { control, setValue, handleSubmit, getValues } = methods;

  // Visualize state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [graphData, setGraphData] = useState<Record<ParametersType, Deque>>({} as any);
  const [tableData, setTableData] = useState<ParsedRow[]>([]);
  const [selectedTableRows, setSelectedTableRows] = useState<Set<number>>(new Set());
  const [selectedDevices, setSelectedDevices] = useState<{ label: string; value: number }[]>([]);
  const [selectedParameters, setSelectedParameters] = useState<ParametersType[]>([]);
  const [activeGraphParameters, setActiveGraphParameters] = useState<ParametersType[]>([]);
  const [tableHeaders, setTableHeaders] = useState<string[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Global time range for the graph
  const [globalTimeRange, setGlobalTimeRange] = useState<{ start: number; end: number } | null>(
    null
  );
  const [dataFetchParams, setDataFetchParams] = useState<{
    start: number;
    end: number;
    devices: number[];
    dataPointsDifference: string;
  } | null>(null);

  // Refs for zoom-to-fetch: debounce and avoid refetch when range is nearly unchanged
  const zoomDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dataFetchParamsRef = useRef(dataFetchParams);
  dataFetchParamsRef.current = dataFetchParams;

  // Full report range: set when we load the full report (used for zoom % and annotation reset)
  const [fullReportRange, setFullReportRange] = useState<{ start: number; end: number } | null>(
    null
  );
  const fullReportRangeRef = useRef<{ start: number; end: number } | null>(null);
  fullReportRangeRef.current = fullReportRange;

  // Resizable divider state
  const DEFAULT_TABLE_HEIGHT = 33.33; // Default to 1/3
  const [tableHeightPercentage, setTableHeightPercentage] = useState(DEFAULT_TABLE_HEIGHT);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Handle divider drag
  const handleDividerMouseDown = (e: React.MouseEvent): void => {
    e.preventDefault();
    setIsDragging(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent): void => {
      if (!isDragging || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const containerHeight = containerRect.height;
      const yPosition = e.clientY - containerRect.top;

      // Calculate percentage (table height)
      // We need to account for the divider height (8px) and the fact that we want table height from bottom
      const dividerOffset = 8; // Height of the divider
      const tableStartPosition = yPosition + dividerOffset;

      // Calculate table height percentage
      const remainingHeight = containerHeight - tableStartPosition;
      const calculatedPercentage = (remainingHeight / containerHeight) * 100;

      // Allow dragging in both directions with reasonable limits
      // Constrain: minimum 15%, maximum 70% (can exceed default)
      const newTableHeightPercentage = Math.max(
        15, // Minimum 15%
        Math.min(70, calculatedPercentage) // Maximum 70%, can exceed default
      );

      setTableHeightPercentage(newTableHeightPercentage);
    };

    const handleMouseUp = (): void => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging]);

  // Calculate predominant device type for priority coloring
  const reportDeviceType = useMemo(() => {
    const isAllSelected = selectedDevices.some((d) => d.value === 0);
    let relevantDevices = [];

    if (isAllSelected) {
      relevantDevices = devices;
    } else {
      const selectedIds = new Set(selectedDevices.map((d) => d.value));
      relevantDevices = devices.filter((d) => selectedIds.has(d.serialNumber));
    }

    if (relevantDevices.length === 0) return DeviceType.MIXED;

    const firstType = relevantDevices[0].deviceType;
    const allSame = relevantDevices.every((d) => d.deviceType === firstType);

    return allSame ? firstType : DeviceType.MIXED;
  }, [devices, selectedDevices]);

  // Track active parameters from graph legend
  useEffect(() => {
    const updateActiveParameters = (): void => {
      const filterData = getAppliedFilter(Filters.histogramParameter);
      if (filterData?.data && Array.isArray(filterData.data) && filterData.data.length > 0) {
        setActiveGraphParameters(filterData.data as ParametersType[]);
      } else if (Object.keys(graphData).length > 0) {
        // If no filter data but graph data exists, use all available parameters
        const availableParams = Object.keys(graphData) as ParametersType[];
        setActiveGraphParameters(availableParams);
      } else if (selectedParameters.length > 0) {
        // Fallback to selected parameters
        setActiveGraphParameters(selectedParameters);
      }
    };

    // Initial read
    updateActiveParameters();

    // Listen to storage events
    const handleStorageChange = (e: StorageEvent): void => {
      if (e.key?.includes('histogramParameter')) {
        updateActiveParameters();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also poll for changes (in case storage event doesn't fire for same-window changes)
    const interval = setInterval(updateActiveParameters, 500);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [selectedParameters, graphData]);

  // Filter selectedParameters to only include those active in graph
  const visibleParameters = useMemo(() => {
    if (activeGraphParameters.length === 0) {
      return selectedParameters;
    }
    return selectedParameters.filter((param) => activeGraphParameters.includes(param));
  }, [selectedParameters, activeGraphParameters]);

  // Store the original file for upload
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [loadedReportKey, setLoadedReportKey] = useState<string | null>(null);

  const loadReports = async (): Promise<void> => {
    try {
      setLoadingReports(true);
      const response = await getAllExternalReports();
      if (response.data) {
        setReports(response.data);
      }
    } catch (err) {
      console.error('Error loading reports:', err);
      toast.error('Failed to load reports');
    } finally {
      setLoadingReports(false);
    }
  };

  const loadReportById = async (key: string): Promise<void> => {
    try {
      setLoadingReports(true);
      const reportResponse = await getExternalReportByKey(key);
      if (!reportResponse.data) {
        throw new Error('Report data not found');
      }

      const report = reportResponse.data;
      const fileKey = report.key;

      // Download the file from S3 using the key
      const downloadResponse = await downloadS3File(fileKey);

      // The response should be a Blob when responseType: 'blob' is set
      // Handle binary data properly
      let blob: Blob;
      if (downloadResponse.data instanceof Blob) {
        blob = downloadResponse.data;
      } else if (
        downloadResponse.data &&
        typeof downloadResponse.data === 'object' &&
        'byteLength' in downloadResponse.data
      ) {
        // If it's an ArrayBuffer-like object, convert to Blob
        blob = new Blob([downloadResponse.data as ArrayBuffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
      } else if (typeof downloadResponse.data === 'string') {
        // If it's a string (base64 or binary string), convert to Blob
        // This shouldn't happen with responseType: 'blob', but handle it just in case
        const binaryString = atob(downloadResponse.data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i += 1) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        blob = new Blob([bytes], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
      } else {
        // Fallback: try to create blob from whatever we got
        blob = new Blob([downloadResponse.data as any], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
      }

      // Extract filename from key or use default
      const fileName = fileKey.split('/').pop() || fileKey || 'report.xlsx';
      const file = new File([blob], fileName, {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      // Store the file
      setOriginalFile(file);

      // Process the file to extract Excel data (without going to setup step)
      const rows = await readXlsxFile(file);
      if (rows && rows.length > 0) {
        // Filter out empty headers
        const headers = rows[0]
          .map(String)
          .filter((h) => h && h.trim() !== '' && h !== 'null' && h !== 'undefined');

        // Remove duplicate headers if any
        const uniqueHeaders = Array.from(new Set(headers));

        const data = rows.slice(1);
        setExcelData({ headers: uniqueHeaders, rows: data });

        // Set form values from report data
        const offsetMinutes = report.timeOffset || parseInt(TIME_OFFSETS[0].value, 10);
        setValue('timeOffset', offsetMinutes.toString());

        // Auto-detect columns or use saved ones if available
        const startCol =
          uniqueHeaders.find(
            (h) =>
              h.toLowerCase().includes('test begin date') || h.toLowerCase().includes('start time')
          ) || uniqueHeaders[0];
        const endCol =
          uniqueHeaders.find(
            (h) => h.toLowerCase().includes('test end date') || h.toLowerCase().includes('end time')
          ) ||
          uniqueHeaders[1] ||
          uniqueHeaders[0];

        if (startCol) setValue('startTimeColumn', startCol);
        if (endCol) setValue('endTimeColumn', endCol);

        // Set devices from report.defaultDevices
        if (report.defaultDevices && report.defaultDevices.length > 0) {
          const deviceOptions = report.defaultDevices.map((deviceId: string) => {
            const device = devices.find((d) => d.serialNumber.toString() === deviceId);
            return {
              label: device?.deviceName || deviceId,
              value: deviceId
            };
          });
          setValue('devices', deviceOptions);
        } else {
          setValue('devices', [{ label: 'All', value: '0' }]);
        }

        // Process table data
        const headerMap = new Map(uniqueHeaders.map((h, i) => [h, i]));
        const startIdx = headerMap.get(startCol);
        const endIdx = headerMap.get(endCol);

        if (startIdx !== undefined && endIdx !== undefined) {
          const processedRows: ParsedRow[] = data.map((row, index) => {
            const obj: ParsedRow = { originalIndex: index };
            uniqueHeaders.forEach((h, i) => {
              obj[h] = row[i];
            });
            return obj;
          });

          setTableData(processedRows);
          // Select all rows by default
          setSelectedTableRows(new Set(processedRows.map((_, i) => i)));

          // Set reordered headers for display (End Time next to Start Time)
          let orderedHeaders = [...uniqueHeaders];
          const sIdx = orderedHeaders.indexOf(startCol);
          const eIdx = orderedHeaders.indexOf(endCol);

          if (sIdx !== -1 && eIdx !== -1) {
            const newHeaders = orderedHeaders.filter((h) => h !== endCol);
            const newSIdx = newHeaders.indexOf(startCol);
            newHeaders.splice(newSIdx + 1, 0, endCol);
            orderedHeaders = newHeaders;
          }
          setTableHeaders(orderedHeaders);
        }

        // Set up time range and data fetch params from report
        // Calculate time range from the Excel data using the stored column names
        const reportStartCol = report.startTimeColumn;
        const reportEndCol = report.endTimeColumn;
        const reportOffset = report.timeOffset || parseInt(TIME_OFFSETS[0].value, 10);

        // Set form values with the stored column names from the report
        setValue('startTimeColumn', reportStartCol);
        setValue('endTimeColumn', reportEndCol);

        // Calculate global time range from the Excel data
        const headerMapForTime = new Map(uniqueHeaders.map((h, i) => [h, i]));
        const startIdxForTime = headerMapForTime.get(reportStartCol);
        const endIdxForTime = headerMapForTime.get(reportEndCol);

        let globalStart: number | undefined;
        let globalEnd: number | undefined;

        if (startIdxForTime !== undefined && endIdxForTime !== undefined) {
          const processedRowsForTime: ParsedRow[] = data.map((row, index) => {
            const obj: ParsedRow = { originalIndex: index };
            uniqueHeaders.forEach((h, i) => {
              obj[h] = row[i];
            });
            return obj;
          });

          // Sort by Start Time ascending
          const sortedByStart = [...processedRowsForTime].sort((a, b) => {
            const dateA = moment(a[reportStartCol]).valueOf();
            const dateB = moment(b[reportStartCol]).valueOf();
            return dateA - dateB;
          });

          // Sort by End Time descending
          const sortedByEnd = [...processedRowsForTime].sort((a, b) => {
            const dateA = moment(a[reportEndCol]).valueOf();
            const dateB = moment(b[reportEndCol]).valueOf();
            return dateB - dateA;
          });

          if (sortedByStart.length > 0 && sortedByEnd.length > 0) {
            const firstStart = moment(sortedByStart[0][reportStartCol]).valueOf();
            const lastEnd = moment(sortedByEnd[0][reportEndCol]).valueOf();

            // Apply offset
            globalStart = moment(firstStart).subtract(reportOffset, 'minutes').valueOf();
            globalEnd = moment(lastEnd).add(reportOffset, 'minutes').valueOf();

            setGlobalTimeRange({ start: globalStart, end: globalEnd });
          }
        }

        // Prepare devices for fetch
        let deviceIds: number[] = [];
        if (report.defaultDevices && report.defaultDevices.length > 0) {
          deviceIds = report.defaultDevices
            .filter((d: string) => d != null && d !== '')
            .map((d: string) => parseInt(d, 10))
            .filter((n: number) => !Number.isNaN(n));
        }

        // Set selected devices for the graph page control
        // Only set if devices are available, otherwise devices might not be loaded yet
        if (devices.length > 0) {
          let selectedDevsForControl;
          if (deviceIds.length === 0) {
            selectedDevsForControl = [{ label: 'All', value: 0 }];
          } else {
            selectedDevsForControl = devices
              .filter((d) => deviceIds.includes(d.serialNumber))
              .map((d) => ({
                label: d.deviceName || d.serialNumber.toString(),
                value: d.serialNumber
              }));
            // If no devices matched, fall back to showing device IDs
            if (selectedDevsForControl.length === 0) {
              selectedDevsForControl = report.defaultDevices
                .filter(
                  (deviceId: string) =>
                    deviceId != null && deviceId !== '' && !Number.isNaN(parseInt(deviceId, 10))
                )
                .map((deviceId: string) => ({
                  label: `Device ${deviceId}`,
                  value: parseInt(deviceId, 10)
                }));
            }
            // If still empty after filtering, default to All
            if (selectedDevsForControl.length === 0) {
              selectedDevsForControl = [{ label: 'All', value: 0 }];
            }
          }
          setSelectedDevices(selectedDevsForControl);
        }

        // Set data fetch params to trigger graph data loading
        if (globalStart !== undefined && globalEnd !== undefined) {
          const dpOptions = getResolutionOptions(globalStart, globalEnd);
          setDataFetchParams({ start: globalStart, end: globalEnd, devices: deviceIds, dataPointsDifference: dpOptions[0].value });
          setFullReportRange({ start: globalStart, end: globalEnd });
          fullReportRangeRef.current = { start: globalStart, end: globalEnd };
        }

        // Default parameters
        if (parameters.length > 0 && selectedParameters.length === 0) {
          setSelectedParameters([parameters[0].parameterName]);
        }

        // Store the loaded report key to track that we've loaded a report
        setLoadedReportKey(key);

        // Go directly to visualize step since we have all the data
        setStep('visualize');
      } else {
        throw new Error('Invalid file data');
      }
    } catch (err) {
      console.error('Error loading report:', err);
      toast.error('Failed to load report');
      setStep('list');
    } finally {
      setLoadingReports(false);
    }
  };

  // Load reports list
  useEffect(() => {
    if (step === 'list' && !id) {
      // Clear loaded report key when going back to list
      setLoadedReportKey(null);
      loadReports();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, id]);

  // Load report by key if id is provided
  useEffect(() => {
    if (id && step === 'list' && devices.length > 0) {
      loadReportById(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, devices]);

  // Helper to process file
  const processFile = async (file: File): Promise<void> => {
    try {
      setOriginalFile(file);
      const rows = await readXlsxFile(file);
      if (rows && rows.length > 0) {
        // Filter out empty headers to avoid issues with dropdowns
        const headers = rows[0]
          .map(String)
          .filter((h) => h && h.trim() !== '' && h !== 'null' && h !== 'undefined');

        // Remove duplicate headers if any
        const uniqueHeaders = Array.from(new Set(headers));

        const data = rows.slice(1);
        setExcelData({ headers: uniqueHeaders, rows: data });
        setStep('setup');

        // Auto-detect columns
        const startCol = uniqueHeaders.find(
          (h) =>
            h.toLowerCase().includes('test begin date') || h.toLowerCase().includes('start time')
        );
        const endCol = uniqueHeaders.find(
          (h) => h.toLowerCase().includes('test end date') || h.toLowerCase().includes('end time')
        );

        if (startCol) setValue('startTimeColumn', startCol);
        if (endCol) setValue('endTimeColumn', endCol);
      }
    } catch (e) {
      console.error('Error reading excel file:', e);
    }
  };

  // Initial device list for dropdown
  const deviceOptionList = useMemo(
    () => [
      { label: 'All', value: '0' },
      ...devices
        .map((d) => ({
          label: d.deviceName || d.serialNumber.toString(),
          value: d.serialNumber.toString()
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    ],
    [devices]
  );

  const generateReport = async (data: SetupFormData): Promise<void> => {
    if (!excelData || !originalFile) return;

    try {
      // First, upload the file to S3
      setLoading(true);
      const uploadResponse = await uploadS3File(originalFile);

      // Extract the key from the response (presignedUrl is also available if needed)
      const fileKey = uploadResponse.data?.fileKey;

      const startCol = data.startTimeColumn;
      const endCol = data.endTimeColumn;
      const offsetMinutes = parseInt(data.timeOffset, 10);

      // Parse rows into objects with dates
      const headerMap = new Map(excelData.headers.map((h, i) => [h, i]));
      const startIdx = headerMap.get(startCol);
      const endIdx = headerMap.get(endCol);

      if (startIdx === undefined || endIdx === undefined) {
        console.error('Invalid columns selected');
        return;
      }

      const processedRows: ParsedRow[] = excelData.rows.map((row, index) => {
        const obj: ParsedRow = { originalIndex: index };
        excelData.headers.forEach((h, i) => {
          obj[h] = row[i];
        });
        return obj;
      });

      // Set reordered headers for display (End Time next to Start Time)
      let orderedHeaders = [...excelData.headers];
      const sIdx = orderedHeaders.indexOf(startCol);
      const eIdx = orderedHeaders.indexOf(endCol);

      if (sIdx !== -1 && eIdx !== -1) {
        const newHeaders = orderedHeaders.filter((h) => h !== endCol);
        const newSIdx = newHeaders.indexOf(startCol);
        newHeaders.splice(newSIdx + 1, 0, endCol);
        orderedHeaders = newHeaders;
      }
      setTableHeaders(orderedHeaders);

      // Determine global start/end times
      // Sort by Start Time ascending
      const sortedByStart = [...processedRows].sort((a, b) => {
        const dateA = moment(a[startCol]).valueOf();
        const dateB = moment(b[startCol]).valueOf();
        return dateA - dateB;
      });

      // Sort by End Time descending
      const sortedByEnd = [...processedRows].sort((a, b) => {
        const dateA = moment(a[endCol]).valueOf();
        const dateB = moment(b[endCol]).valueOf();
        return dateB - dateA; // Descending
      });

      if (sortedByStart.length === 0) return;

      const firstStart = moment(sortedByStart[0][startCol]).valueOf();
      const lastEnd = moment(sortedByEnd[0][endCol]).valueOf();

      // Apply offset
      const globalStart = moment(firstStart).subtract(offsetMinutes, 'minutes').valueOf();
      const globalEnd = moment(lastEnd).add(offsetMinutes, 'minutes').valueOf();

      setGlobalTimeRange({ start: globalStart, end: globalEnd });
      setTableData(processedRows);
      // Select all rows by default
      setSelectedTableRows(new Set(processedRows.map((_, i) => i)));

      // Prepare devices for fetch
      let deviceIds: number[] = [];
      if (data.devices.some((d) => d.value === '0')) {
        // All devices - pass empty array as per API requirement
        deviceIds = [];
      } else {
        deviceIds = data.devices.map((d) => parseInt(d.value, 10));
      }

      // Set selected devices for the graph page control
      let selectedDevsForControl;
      if (deviceIds.length === 0) {
        // All devices selected
        selectedDevsForControl = [{ label: 'All', value: 0 }];
      } else {
        selectedDevsForControl = devices
          .filter((d) => deviceIds.includes(d.serialNumber))
          .map((d) => ({
            label: d.deviceName || d.serialNumber.toString(),
            value: d.serialNumber
          }));
      }
      setSelectedDevices(selectedDevsForControl);

      // Default parameters (first few)
      if (parameters.length > 0 && selectedParameters.length === 0) {
        // Pick some default parameters if none selected yet
        setSelectedParameters([parameters[0].parameterName]);
      }

      // Trigger fetch
      const dpOptions = getResolutionOptions(globalStart, globalEnd);
      setDataFetchParams({ start: globalStart, end: globalEnd, devices: deviceIds, dataPointsDifference: dpOptions[0].value });
      setFullReportRange({ start: globalStart, end: globalEnd });
      fullReportRangeRef.current = { start: globalStart, end: globalEnd };

      // Create external report entry
      const defaultDevices = data.devices.filter((d) => d.value !== '0').map((d) => d.value);

      await createExternalReport({
        key: fileKey,
        startTimeColumn: startCol, // Store column name, not timestamp
        endTimeColumn: endCol, // Store column name, not timestamp
        defaultDevices: defaultDevices.length > 0 ? defaultDevices : [],
        timeOffset: offsetMinutes
      });

      setLoadedReportKey(fileKey);
      setStep('visualize');
      toast.success('Report created successfully');
    } catch (err) {
      console.error('Error generating report:', err);
      toast.error('Failed to create report');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReport = async (key: string): Promise<void> => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Are you sure you want to delete this report?')) return;

    try {
      await deleteExternalReport(key);
      toast.success('Report deleted successfully');
      loadReports();
    } catch (err) {
      console.error('Error deleting report:', err);
      toast.error('Failed to delete report');
    }
  };

  const handleViewReport = (key: string): void => {
    navigate(`/reports/${key}`);
  };

  // Fetch data when params change; cancel previous request when selection/params change
  useEffect(function fetchSummaryEffect() {
    if (!dataFetchParams || step !== 'visualize') return undefined;

    const abortController = new AbortController();
    const { signal } = abortController;

    const fetch = async (): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const response = await getDeviceSummaryOverTime(
          {
            startDate: dataFetchParams.start,
            endDate: dataFetchParams.end,
            devices: dataFetchParams.devices,
            dataPointsDifference: dataFetchParams.dataPointsDifference
          } as any,
          { signal }
        );

        if (signal.aborted) return;

        if (response?.data?.data) {
          // Process response - store ALL parameter data from the API
          setGraphData((prevGraphData) => {
            const newGraphData: Record<ParametersType, Deque> = { ...prevGraphData } as any;

            response.data.data.forEach((paramData: any) => {
              if (paramData && paramData.name && paramData.data) {
                const paramName = paramData.name as ParametersType;
                const value = changeZeroValuesToNull(paramData.data);
                (newGraphData as any)[paramName] = new Deque();
                (newGraphData as any)[paramName].add(...value);
              }
            });

            return newGraphData;
          });
        } else {
          throw new Error('Failed to fetch data');
        }
      } catch (err) {
        const { name, code } = err as { name?: string; code?: string };
        if (name === 'CanceledError' || code === 'ERR_CANCELED' || signal.aborted) {
          return;
        }
        console.error(err);
        setError('Failed to generate report. Please try again.');
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    };

    fetch();
    return function cleanup() {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataFetchParams, step]);

  // When user zooms in or out, refetch data for the visible time range after they stop (debounced).
  // Refetch on zoom out so that after clicking an annotation (small range) they can zoom out to see the full report again.
  // Skip refetch if the new range is nearly the same as the last fetch (except when zooming out - always allow).
  // Pass { immediate: true } from the zoom-out button to refetch immediately (no debounce).
  const handleZoomTimeRangeChange = useCallback(
    (start: number, end: number, options?: { immediate?: boolean }) => {
      const doRefetch = (): void => {
        const prev = dataFetchParamsRef.current;
        if (!prev) return;
        const prevDuration = prev.end - prev.start;
        const newDuration = end - start;
        const isZoomingOut = newDuration > prevDuration;
        if (!isZoomingOut) {
          const threshold = Math.max(prevDuration * 0.03, 60 * 1000); // 3% of range or 1 min
          if (
            Math.abs(prev.start - start) < threshold &&
            Math.abs(prev.end - end) < threshold
          ) {
            return; // Range too similar, skip refetch
          }
        }
        const opts = getResolutionOptions(start, end);
        const nextDataPointsDiff = opts.some((o) => o.value === prev.dataPointsDifference)
          ? prev.dataPointsDifference
          : opts[0].value;
        setDataFetchParams({ start, end, devices: prev.devices, dataPointsDifference: nextDataPointsDiff });
      };

      if (options?.immediate) {
        if (zoomDebounceRef.current) {
          clearTimeout(zoomDebounceRef.current);
          zoomDebounceRef.current = null;
        }
        doRefetch();
        return;
      }

      if (zoomDebounceRef.current) clearTimeout(zoomDebounceRef.current);
      zoomDebounceRef.current = setTimeout(() => {
        zoomDebounceRef.current = null;
        doRefetch();
      }, 700);
    },
    []
  );

  // Clear zoom debounce on unmount
  useEffect(
    () => () => {
      if (zoomDebounceRef.current) clearTimeout(zoomDebounceRef.current);
    },
    []
  );

  // Device options list for visualize step
  const visualizeDeviceOptionList = useMemo(
    () => [
      { label: 'All', value: '0' },
      ...devices
        .map((d) => ({
          label: d.deviceName || d.serialNumber.toString(),
          value: d.serialNumber.toString()
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    ],
    [devices]
  );

  // Update selected devices when devices are loaded and we have a report loaded
  useEffect(() => {
    if (loadedReportKey && devices.length > 0 && step === 'visualize') {
      // Re-fetch the report to get device info and update selectedDevices
      const updateDevices = async (): Promise<void> => {
        try {
          const reportResponse = await getExternalReportByKey(loadedReportKey);
          if (reportResponse.data) {
            const report = reportResponse.data;
            const deviceIds: number[] =
              report.defaultDevices && report.defaultDevices.length > 0
                ? report.defaultDevices
                    .filter((d: string) => d != null && d !== '')
                    .map((d: string) => parseInt(d, 10))
                    .filter((n: number) => !Number.isNaN(n))
                : [];

            let selectedDevsForControl;
            if (deviceIds.length === 0) {
              selectedDevsForControl = [{ label: 'All', value: 0 }];
            } else {
              selectedDevsForControl = devices
                .filter((d) => deviceIds.includes(d.serialNumber))
                .map((d) => ({
                  label: d.deviceName || d.serialNumber.toString(),
                  value: d.serialNumber
                }));
              // If no devices matched, fall back to showing device IDs
              if (selectedDevsForControl.length === 0) {
                selectedDevsForControl = report.defaultDevices
                  .filter(
                    (deviceId: string) =>
                      deviceId != null && deviceId !== '' && !Number.isNaN(parseInt(deviceId, 10))
                  )
                  .map((deviceId: string) => ({
                    label: `Device ${deviceId}`,
                    value: parseInt(deviceId, 10)
                  }));
              }
              // If still empty after filtering, default to All
              if (selectedDevsForControl.length === 0) {
                selectedDevsForControl = [{ label: 'All', value: 0 }];
              }
            }
            setSelectedDevices(selectedDevsForControl);
          }
        } catch (err) {
          console.error('Error updating devices:', err);
        }
      };
      updateDevices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices, loadedReportKey, step]);

  // Handle device change in visualize view
  const handleDeviceChangeFromDropdown = (newDevices: { label: string; value: string }[]): void => {
    // Convert dropdown format to internal format
    const convertedDevices = newDevices.map((d) => ({
      label: d.label,
      value: d.value === '0' ? 0 : parseInt(d.value, 10)
    }));
    setSelectedDevices(convertedDevices);
    if (globalTimeRange) {
      let deviceIds: number[] = [];
      if (convertedDevices.some((d) => d.value === 0)) {
        deviceIds = [];
      } else {
        deviceIds = convertedDevices.map((d) => d.value);
      }

      setDataFetchParams({
        start: globalTimeRange.start,
        end: globalTimeRange.end,
        devices: deviceIds,
        dataPointsDifference: dataFetchParamsRef.current?.dataPointsDifference || '1m'
      });
      setFullReportRange({ start: globalTimeRange.start, end: globalTimeRange.end });
      fullReportRangeRef.current = {
        start: globalTimeRange.start,
        end: globalTimeRange.end
      };
    }
  };

  // Handle time offset change in visualize view
  const handleTimeOffsetChange = (newOffset: string): void => {
    console.log('UPDATED');
    setValue('timeOffset', newOffset);
    const offsetMinutes = parseInt(newOffset, 10);

    if (!excelData || selectedTableRows.size === 0) return;

    const startCol = getValues('startTimeColumn');
    const endCol = getValues('endTimeColumn');

    const selectedRows = tableData.filter((_, i) => selectedTableRows.has(i));
    if (selectedRows.length === 0) return;

    // Sort by Start Time ascending
    const sortedByStart = [...selectedRows].sort((a, b) => {
      const dateA = moment(a[startCol]).valueOf();
      const dateB = moment(b[startCol]).valueOf();
      return dateA - dateB;
    });

    // Sort by End Time descending
    const sortedByEnd = [...selectedRows].sort((a, b) => {
      const dateA = moment(a[endCol]).valueOf();
      const dateB = moment(b[endCol]).valueOf();
      return dateB - dateA;
    });

    const firstStart = moment(sortedByStart[0][startCol]).valueOf();
    const lastEnd = moment(sortedByEnd[0][endCol]).valueOf();

    const globalStart = moment(firstStart).subtract(offsetMinutes, 'minutes').valueOf();
    const globalEnd = moment(lastEnd).add(offsetMinutes, 'minutes').valueOf();

    setGlobalTimeRange({ start: globalStart, end: globalEnd });

    let deviceIds: number[] = [];
    if (selectedDevices.some((d) => d.value === 0)) {
      deviceIds = [];
    } else {
      deviceIds = selectedDevices.map((d) => d.value);
    }

    setDataFetchParams({
      start: globalStart,
      end: globalEnd,
      devices: deviceIds,
      dataPointsDifference: dataFetchParamsRef.current?.dataPointsDifference || '1m'
    });
    setFullReportRange({ start: globalStart, end: globalEnd });
    fullReportRangeRef.current = { start: globalStart, end: globalEnd };

    // Persist the new offset to the database
    if (loadedReportKey) {
      updateExternalReport(loadedReportKey, {
        startTimeColumn: getValues('startTimeColumn'),
        endTimeColumn: getValues('endTimeColumn'),
        defaultDevices: selectedDevices.filter((d) => d.value !== 0).map((d) => d.value.toString()),
        timeOffset: offsetMinutes
      }).catch((err) => console.error('Failed to update time offset:', err));
    } else {
      console.log('No report key');
    }
  };

  // Generate annotations based on selected rows
  const annotations = useMemo((): any => {
    if (step !== 'visualize' || !excelData || !selectedTableRows.size) return {};

    const startCol = getValues('startTimeColumn');
    const endCol = getValues('endTimeColumn');

    const annotationsObj: any = {};

    tableData.forEach((row, index) => {
      if (selectedTableRows.has(index)) {
        const startTime = moment(row[startCol]).valueOf();
        const endTime = moment(row[endCol]).valueOf();
        const colorIndex = index % ROW_COLORS.length;

        const duration = endTime - startTime;
        const buffer = duration * 0.25;
        const zoomedMin = startTime - buffer;
        const zoomedMax = endTime + buffer;

        annotationsObj[`box${index}`] = {
          type: 'box',
          xMin: startTime,
          xMax: endTime,
          xScaleID: 'x',
          yScaleID: 'y',
          backgroundColor: ROW_COLORS[colorIndex],
          borderColor: ROW_BORDER_COLORS[colorIndex],
          borderWidth: 1,
          drawTime: 'beforeDatasetsDraw',
          click() {
            const prev = dataFetchParamsRef.current;
            const full = fullReportRangeRef.current;
            const isZoomedToThisBox =
              prev &&
              full &&
              prev.end - prev.start < full.end - full.start &&
              Math.abs(prev.start - zoomedMin) < 60000 &&
              Math.abs(prev.end - zoomedMax) < 60000;
            if (isZoomedToThisBox && full) {
              setDataFetchParams((p) =>
                p ? { start: full.start, end: full.end, devices: p.devices, dataPointsDifference: getResolutionOptions(full.start, full.end)[0].value } : null
              );
            } else {
              setDataFetchParams((p) =>
                p ? { start: zoomedMin, end: zoomedMax, devices: p.devices, dataPointsDifference: getResolutionOptions(zoomedMin, zoomedMax)[0].value } : null
              );
            }
          }
        };
      }
    });

    return annotationsObj;
  }, [step, excelData, tableData, selectedTableRows, getValues]);

  const toggleRow = (index: number): void => {
    const newSet = new Set(selectedTableRows);
    if (newSet.has(index)) newSet.delete(index);
    else newSet.add(index);
    setSelectedTableRows(newSet);

    // Recalculate time range based on selected rows
    if (newSet.size > 0 && excelData) {
      const startCol = getValues('startTimeColumn');
      const endCol = getValues('endTimeColumn');
      const offsetMinutes = parseInt(getValues('timeOffset'), 10);

      const selectedRows = tableData.filter((_, i) => newSet.has(i));

      // Sort by Start Time ascending
      const sortedByStart = [...selectedRows].sort((a, b) => {
        const dateA = moment(a[startCol]).valueOf();
        const dateB = moment(b[startCol]).valueOf();
        return dateA - dateB;
      });

      // Sort by End Time descending
      const sortedByEnd = [...selectedRows].sort((a, b) => {
        const dateA = moment(a[endCol]).valueOf();
        const dateB = moment(b[endCol]).valueOf();
        return dateB - dateA; // Descending
      });

      const firstStart = moment(sortedByStart[0][startCol]).valueOf();
      const lastEnd = moment(sortedByEnd[0][endCol]).valueOf();

      // Apply offset
      const globalStart = moment(firstStart).subtract(offsetMinutes, 'minutes').valueOf();
      const globalEnd = moment(lastEnd).add(offsetMinutes, 'minutes').valueOf();

      setGlobalTimeRange({ start: globalStart, end: globalEnd });

      // Update fetch params
      let deviceIds: number[] = [];
      if (selectedDevices.some((d) => d.value === 0)) {
        deviceIds = [];
      } else {
        deviceIds = selectedDevices.map((d) => d.value);
      }

      setDataFetchParams({
        start: globalStart,
        end: globalEnd,
        devices: deviceIds,
        dataPointsDifference: dataFetchParamsRef.current?.dataPointsDifference || '1m'
      });
      setFullReportRange({ start: globalStart, end: globalEnd });
      fullReportRangeRef.current = { start: globalStart, end: globalEnd };
    }
  };

  const toggleAllRows = (): void => {
    if (selectedTableRows.size === tableData.length) {
      setSelectedTableRows(new Set());
      // If no rows selected, we probably don't want to fetch or fetch everything?
      // Sticking to current behavior: don't update fetch params if nothing selected or maybe just keep last state
    } else {
      const newSet = new Set(tableData.map((_, i) => i));
      setSelectedTableRows(newSet);

      // Recalculate for all rows (same logic as generateReport)
      if (excelData) {
        const startCol = getValues('startTimeColumn');
        const endCol = getValues('endTimeColumn');
        const offsetMinutes = parseInt(getValues('timeOffset'), 10);

        // Sort by Start Time ascending
        const sortedByStart = [...tableData].sort((a, b) => {
          const dateA = moment(a[startCol]).valueOf();
          const dateB = moment(b[startCol]).valueOf();
          return dateA - dateB;
        });

        // Sort by End Time descending
        const sortedByEnd = [...tableData].sort((a, b) => {
          const dateA = moment(a[endCol]).valueOf();
          const dateB = moment(b[endCol]).valueOf();
          return dateB - dateA;
        });

        const firstStart = moment(sortedByStart[0][startCol]).valueOf();
        const lastEnd = moment(sortedByEnd[0][endCol]).valueOf();

        const globalStart = moment(firstStart).subtract(offsetMinutes, 'minutes').valueOf();
        const globalEnd = moment(lastEnd).add(offsetMinutes, 'minutes').valueOf();

        setGlobalTimeRange({ start: globalStart, end: globalEnd });

        let deviceIds: number[] = [];
        if (selectedDevices.some((d) => d.value === 0)) {
          deviceIds = [];
        } else {
          deviceIds = selectedDevices.map((d) => d.value);
        }

        setDataFetchParams({
          start: globalStart,
          end: globalEnd,
          devices: deviceIds,
          dataPointsDifference: dataFetchParamsRef.current?.dataPointsDifference || '1m'
        });
        setFullReportRange({ start: globalStart, end: globalEnd });
        fullReportRangeRef.current = { start: globalStart, end: globalEnd };
      }
    }
  };

  const calculateMetricAverage = (
    data: Record<string, any> | undefined,
    param: ParametersType
  ): string => {
    if (!data) return '-';
    const deviceKeys = Object.keys(data);
    if (deviceKeys.length === 0) return '-';

    let sum = 0;
    let count = 0;

    deviceKeys.forEach((key) => {
      const deviceEntry = data[key];
      if (deviceEntry?.parameters) {
        const val = deviceEntry.parameters[param];
        if (typeof val === 'number') {
          sum += val;
          count += 1;
        }
      }
    });

    if (count === 0) return '-';
    return getDisplayableValue(param, sum / count);
  };

  const handleDownloadExcel = async (): Promise<void> => {
    if (!tableData || tableData.length === 0 || selectedTableRows.size === 0) return;

    try {
      setIsDownloading(true);
      const rowsToDownload = tableData.filter((_, index) => selectedTableRows.has(index));

      const startTimeCol = getValues('startTimeColumn');
      const endTimeCol = getValues('endTimeColumn');
      const timeOffsetStr = getValues('timeOffset');
      const timeOffset = parseInt(timeOffsetStr, 10);

      // Resolve device IDs
      let deviceIds: number[] = [];
      if (selectedDevices.some((d) => d.value === 0)) {
        deviceIds = [];
      } else {
        deviceIds = selectedDevices.map((d) => d.value);
      }

      // Fetch metrics for all rows with batching to avoid network congestion
      const enrichedRows: { row: ParsedRow; metrics: any }[] = [];
      const BATCH_SIZE = 5;

      for (let i = 0; i < rowsToDownload.length; i += BATCH_SIZE) {
        const batch = rowsToDownload.slice(i, i + BATCH_SIZE);
        // eslint-disable-next-line no-await-in-loop
        const batchResults = await Promise.all(
          batch.map(async (row) => {
            const start = moment(row[startTimeCol]).valueOf();
            const end = moment(row[endTimeCol]).valueOf();

            const payload = {
              devices: deviceIds,
              startTime: start,
              endTime: end,
              timeDifference: timeOffset
            };

            let metricsData = null;
            try {
              const response = await getDeviceTimeRangeMetrics(payload);
              if (response.data) {
                metricsData = response.data;
              }
            } catch (err) {
              console.error('Error fetching metrics for row download', err);
            }

            return {
              row,
              metrics: metricsData
            };
          })
        );
        enrichedRows.push(...batchResults);
      }

      // Construct AOA with two header rows for sub-columns
      const headerRow1: string[] = [];
      const headerRow2: string[] = [];
      const dataRows: any[][] = [];
      const merges: Array<{ s: { r: number; c: number }; e: { r: number; c: number } }> = [];

      // Build Headers
      const baseHeaders = tableHeaders.length > 0 ? tableHeaders : excelData?.headers || [];
      const visibleParams =
        selectedParameters.length > 0
          ? selectedParameters
          : activeGraphParameters.length > 0
          ? activeGraphParameters
          : [];

      let colIdx = 0;
      baseHeaders.forEach((h) => {
        headerRow1.push(h);
        headerRow2.push('');
        // Merge base header cells across both rows
        merges.push({ s: { r: 0, c: colIdx }, e: { r: 1, c: colIdx } });
        colIdx += 1;
        if (h === endTimeCol) {
          // Time Offset column (merged across both rows)
          headerRow1.push('Time Offset');
          headerRow2.push('');
          merges.push({ s: { r: 0, c: colIdx }, e: { r: 1, c: colIdx } });
          colIdx += 1;

          // Parameter sub-columns
          visibleParams.forEach((param) => {
            const info = parameters.find((p) => p.parameterName === param);
            const paramName = info?.shortName || param;
            const unit = info?.unit || '';
            const label = `${paramName}${unit ? ` (${unit})` : ''}`;
            // Row 1: parameter name spanning 3 columns
            headerRow1.push(label);
            headerRow1.push('');
            headerRow1.push('');
            // Row 2: sub-column headers
            headerRow2.push('- Offset');
            headerRow2.push('Between');
            headerRow2.push('+ Offset');
            // Merge parameter name across 3 columns in row 1
            merges.push({ s: { r: 0, c: colIdx }, e: { r: 0, c: colIdx + 2 } });
            colIdx += 3;
          });
        }
      });

      // Build Data Rows
      enrichedRows.forEach(({ row, metrics }) => {
        const rowValues: any[] = [];
        baseHeaders.forEach((h) => {
          rowValues.push(row[h]);
          if (h === endTimeCol) {
            rowValues.push(`${timeOffsetStr} mins`);

            visibleParams.forEach((param) => {
              const before = calculateMetricAverage(metrics?.beforeStartTime, param);
              const during = calculateMetricAverage(metrics?.duringTime, param);
              const after = calculateMetricAverage(metrics?.afterEndTime, param);
              rowValues.push(before);
              rowValues.push(during);
              rowValues.push(after);
            });
          }
        });
        dataRows.push(rowValues);
      });

      const ws = XLSX.utils.aoa_to_sheet([headerRow1, headerRow2, ...dataRows]);
      ws['!merges'] = merges;
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Report Data');

      const timestamp = moment().format('YYYY-MM-DD_HH-mm-ss');
      XLSX.writeFile(wb, `original_${timestamp}.xlsx`);
    } catch (err) {
      console.error('Download failed', err);
      toast.error('Failed to generate Excel file');
    } finally {
      setIsDownloading(false);
    }
  };

  // Reports List View
  if (step === 'list' && !id) {
    return (
      <div className="flex flex-col items-center justify-center p-4 w-full text-white">
        <div className="w-full flex flex-col bg-background-700 h-[calc(100vh-10vh)] p-8 rounded-lg border border-gray-700">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="p-2 hover:bg-container-500 rounded-full transition-colors"
                title="Back to Dashboard"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <h2 className="text-xl font-bold">External Reports</h2>
            </div>
            <button
              type="button"
              onClick={() => setStep('upload')}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md font-medium transition-colors flex items-center gap-2"
            >
              <Upload className="w-4 h-4" />
              Upload New Report
            </button>
          </div>

          {loadingReports ? (
            <Loader />
          ) : (
            <div className="flex-1 overflow-auto">
              <table className="w-full text-left text-base text-gray-400">
                <thead className="text-xs uppercase bg-container-700 text-gray-200 sticky top-0 z-30 shadow-sm">
                  <tr>
                    <th className="px-4 py-3 whitespace-nowrap">Key</th>
                    <th className="px-4 py-3 whitespace-nowrap">Start Time</th>
                    <th className="px-4 py-3 whitespace-nowrap">End Time</th>
                    <th className="px-4 py-3 whitespace-nowrap">Default Devices</th>
                    <th className="px-4 py-3 whitespace-nowrap">Created At</th>
                    <th className="px-4 py-3 whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                        No reports found. Upload a new report to get started.
                      </td>
                    </tr>
                  ) : (
                    reports.map((report) => (
                      <tr
                        key={report.key}
                        className="bg-container-600/50 hover:bg-container-500 transition-colors border-b border-container-700"
                      >
                        <td
                          onClick={() => handleViewReport(report.key)}
                          className="px-4 py-3 whitespace-nowrap cursor-pointer"
                        >
                          {report.key}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">{report.startTimeColumn}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{report.endTimeColumn}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {report.defaultDevices.length > 0
                            ? report.defaultDevices.join(', ')
                            : 'All Devices'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {report.createdTime
                            ? moment(report.createdTime).format('Do MMM, YYYY')
                            : '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleViewReport(report.key)}
                              className="p-2 hover:bg-container-500 rounded-md transition-colors"
                              title="View Report"
                            >
                              <Eye className="w-4 h-4 text-primary-400" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteReport(report.key)}
                              className="p-2 hover:bg-container-500 rounded-md transition-colors"
                              title="Delete Report"
                            >
                              <Trash2 className="w-4 h-4 text-error-500" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (step === 'upload') {
    return (
      <div className="flex flex-col items-center justify-center p-4 w-full text-white">
        <div className="w-full relative flex flex-col items-center justify-center  bg-background-700 h-[calc(100vh-10vh)] p-8 rounded-lg border border-gray-700 text-center">
          <button
            type="button"
            onClick={() => setStep('list')}
            className="absolute top-4 flex items-center gap-2 text-lg left-4 p-2 hover:bg-container-500 rounded-full transition-colors z-10"
            title="Back to Reports List"
          >
            <ChevronLeft className="w-6 h-6" />
            Back to Reports List
          </button>
          <div className="!w-[300px] mx-auto ">
            <div className="w-16 h-16 bg-container-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Upload className="h-8 w-8 text-gray-400" />
            </div>
            <p className="text-gray-400 mb-6">Upload file to generate a report.</p>
            <label
              htmlFor="file-upload"
              className="flex items-center justify-center w-full px-4 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-md cursor-pointer transition-colors gap-2"
            >
              <FileSpreadsheet className="w-5 h-5" />
              <span>Select File</span>
              <input
                id="file-upload"
                type="file"
                accept=".xlsx, .xls"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && processFile(e.target.files[0])}
              />
            </label>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'setup') {
    const columnOptions = excelData?.headers.map((h) => ({ label: h, value: h })) || [];

    return (
      <div className="flex h-full flex-col items-center justify-center">
        <div className="flex flex-col items-center justify-center h-[calc(100vh-10vh)] w-full p-8 bg-background-700 text-white rounded-lg border border-gray-700 ">
          <div className="w-full max-w-2xl bg-container-600 p-8 rounded-lg border border-gray-700">
            <div className="flex items-center gap-4 mb-6">
              <button
                type="button"
                onClick={() => {
                  if (id) {
                    navigate('/reports');
                  } else {
                    setStep('upload');
                  }
                }}
                className="p-2 hover:bg-container-500 rounded-full transition-colors"
                title="Back"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <h2 className="text-xl font-bold">Configure Report</h2>
            </div>

            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(generateReport)} className="space-y-6">
                <MultiSelectDropdown
                  id="devices"
                  label="Devices (Max 3)"
                  validationObject={{
                    required: 'Please select at least one device',
                    validate: (value: any[]) => {
                      if (value.some((v) => v.value === '0')) return true;
                      if (value.length > 3) return 'You can select a maximum of 3 devices';
                      return true;
                    }
                  }}
                  control={control}
                  options={deviceOptionList}
                  tooltip="Select up to 3 devices to include in the report. Select 'All' to include all devices."
                />

                <div className="grid grid-cols-2 gap-4">
                  <CustomDropdown
                    id="startTimeColumn"
                    label="Start Time Column"
                    options={columnOptions}
                    validationObject={{ required: 'Required' }}
                    tooltip="Select the Excel column that contains the test start time/date"
                  />
                  <CustomDropdown
                    id="endTimeColumn"
                    label="End Time Column"
                    options={columnOptions}
                    validationObject={{ required: 'Required' }}
                    tooltip="Select the Excel column that contains the test end time/date"
                  />
                </div>

                <CustomDropdown
                  id="timeOffset"
                  label="Time Offset (Buffer)"
                  options={TIME_OFFSETS}
                  validationObject={{ required: 'Required' }}
                  tooltip="This is the time period for pre and post data"
                />

                <div className="pt-4 flex justify-end">
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      'Generate Report'
                    )}
                  </button>
                </div>
              </form>
            </FormProvider>
          </div>
        </div>
      </div>
    );
  }

  // Visualize Step
  if (loadingReports) {
    return (
      <div className="flex flex-1 flex-col justify-center items-center h-[calc(100vh-10vh)]">
        <Loader />
      </div>
    );
  }

  return (
    <PlanGate requiredPlan="Professional">
    <div className="flex flex-1 flex-col justify-center items-center">
      <div
        ref={containerRef}
        className="flex flex-col w-full h-[calc(100vh-10vh)] rounded-lg bg-background-700 text-white p-4 pb-0 gap-4 overflow-hidden"
      >
        {/* Header Controls */}
        <div className="flex items-center justify-between shrink-0">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => {
                if (id) {
                  // Clear loaded report key and reset state before navigating
                  setLoadedReportKey(null);
                  // Clear all report-related state
                  setExcelData(null);
                  setTableData([]);
                  setSelectedTableRows(new Set());
                  setGraphData({} as any);
                  setGlobalTimeRange(null);
                  setDataFetchParams(null);
                  setSelectedDevices([]);
                  setSelectedParameters([]);
                  setTableHeaders([]);
                  setOriginalFile(null);
                  // Set step to list and navigate
                  setStep('list');
                  navigate('/reports', { replace: true });
                } else {
                  setStep('setup');
                }
              }}
              className="p-2 hover:bg-container-500 rounded-full transition-colors"
              title="Back to Reports List"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-2 relative">
              <button
                type="button"
                onClick={() => setShowDatePicker(!showDatePicker)}
                className="flex items-center gap-2 px-3 py-[7px] bg-[#0d0d14] border border-[#344054] hover:border-[#84ADFF] rounded-md text-sm text-gray-300 transition-colors"
                title="Zoom to Custom Time Range"
              >
                <Calendar className="w-4 h-4" />
                <span>
                  {dataFetchParams 
                    ? `${moment(dataFetchParams.start).format('DD MMM HH:mm')} - ${moment(dataFetchParams.end).format('DD MMM HH:mm')}`
                    : 'Select Time Range'
                  }
                </span>
              </button>
              {showDatePicker && dataFetchParams && (
                <div className="absolute z-50 top-11 left-0 shadow-lg rounded-md border border-gray-700 bg-container-700 overflow-hidden">
                  <DateRangePicker
                    timeSpanDate={{ startDate: dataFetchParams.start, endDate: dataFetchParams.end }}
                    handleCancel={() => setShowDatePicker(false)}
                    onApply={(value) => {
                      const newDpOptions = getResolutionOptions(value.startDate, value.endDate);
                      const nextDataPointsDiff = newDpOptions.some((o) => o.value === dataFetchParams.dataPointsDifference)
                        ? dataFetchParams.dataPointsDifference
                        : newDpOptions[0].value;
                      
                      setDataFetchParams({
                        ...dataFetchParams,
                        start: value.startDate,
                        end: value.endDate,
                        dataPointsDifference: nextDataPointsDiff
                      });
                      setShowDatePicker(false);
                    }}
                  />
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-500 dark:text-gray-500 text-sm">Devices:</span>
              <div className="w-64">
                <Select
                  value={selectedDevices.map((d) => ({
                    label: d.label,
                    value: d.value === 0 ? '0' : d.value.toString()
                  }))}
                  onChange={(newValue) => {
                    const values: { label: string; value: string }[] = Array.isArray(newValue)
                      ? newValue.map((v) => ({ label: v.label, value: v.value }))
                      : [];
                    if (values.length > 3) return;
                    handleDeviceChangeFromDropdown(values);
                  }}
                  options={visualizeDeviceOptionList}
                  isMulti
                  closeMenuOnSelect={false}
                  styles={{
                    control: (styles) => ({
                      ...styles,
                      background: '#0d0d14',
                      border: '1px solid #344054',
                      boxShadow: '0 !important',
                      '&:hover': {
                        border: '1px solid #84ADFF !important'
                      }
                    }),
                    menuList: (styles) => ({
                      ...styles,
                      background: '#12151f',
                      color: '#667085'
                    }),
                    option: (styles) => ({
                      ...styles,
                      background: '#12151f',
                      cursor: 'pointer',
                      '&:hover': {
                        background: '#1a1d2c',
                        color: '#D0D5DD'
                      }
                    }),
                    multiValue: (styles) => ({
                      ...styles,
                      background: '#1D2939',
                      color: '#B2CCFF'
                    }),
                    multiValueLabel: (styles) => ({
                      ...styles,
                      color: '#B2CCFF'
                    })
                  }}
                  className="bg-container-500"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-500 dark:text-gray-500 text-sm">Offset:</span>
              <select
                value={getValues('timeOffset')}
                onChange={(e) => handleTimeOffsetChange(e.target.value)}
                className="bg-[#0d0d14] border border-[#344054] text-gray-300 text-sm rounded-md px-3 py-[7px] focus:border-primary-400 focus:outline-none hover:border-[#84ADFF] transition-colors cursor-pointer"
              >
                {TIME_OFFSETS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-500 dark:text-gray-500 text-sm">Time Resolution:</span>
              <select
                value={dataFetchParams?.dataPointsDifference || '1m'}
                onChange={(e) => {
                  if (dataFetchParams) {
                    setDataFetchParams({
                      ...dataFetchParams,
                      dataPointsDifference: e.target.value
                    });
                  }
                }}
                disabled={!dataFetchParams}
                className="bg-[#0d0d14] border border-[#344054] text-gray-300 text-sm rounded-md px-3 py-[7px] focus:border-primary-400 focus:outline-none hover:border-[#84ADFF] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {dataFetchParams && getResolutionOptions(dataFetchParams.start, dataFetchParams.end).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={handleDownloadExcel}
              disabled={selectedTableRows.size === 0 || isDownloading}
              className="p-2 hover:bg-container-500 rounded-md transition-colors text-primary-400 disabled:text-gray-600 disabled:cursor-not-allowed"
              title="Download Checked Rows"
            >
              {isDownloading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Download className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>

        {/* Graph Area */}
        <div
          className="rounded-lg min-h-0 overflow-hidden relative flex flex-col"
          style={{ height: `${100 - tableHeightPercentage}%` }}
        >
          {loading || (!!dataFetchParams && Object.keys(graphData).length === 0) ? (
            <Loader />
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <p className="text-error-500 mb-4 font-medium">{error}</p>
              <button
                type="button"
                onClick={() =>
                  setDataFetchParams((prev) => (prev ? { ...prev, start: prev.start } : null))
                }
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md font-medium transition-colors"
              >
                Retry
              </button>
            </div>
          ) : (
            <HistogramWidget
                timeSpan={TimeSpan.Custom}
                extendedLabel="Custom Report"
                onChangeParameter={setSelectedParameters}
                lineChartGraphData={graphData}
                loading={
                  (loading && Object.keys(graphData).length === 0) ||
                  (!!dataFetchParams && Object.keys(graphData).length === 0)
                }
                expanded={false}
                type={DeviceType.MIXED}
                annotations={annotations}
                maxTicksLimit={50}
                showZoomControls={false}
                onZoomTimeRangeChange={handleZoomTimeRangeChange}
                fullReportRange={fullReportRange}
              />
          )}
        </div>
        {/* Resizable Divider */}
        <button
          type="button"
          aria-label="Resize graph and table areas"
          className="relative w-full cursor-row-resize select-none hover:bg-gray-600 active:bg-gray-500 transition-colors group focus:outline-none focus:ring-2 focus:ring-primary-500 border-0 p-0"
          onMouseDown={handleDividerMouseDown}
          onKeyDown={(e) => {
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
              e.preventDefault();
              if (e.key === 'ArrowUp') {
                // Decrease table height (make smaller) - allowed
                setTableHeightPercentage((prev) => Math.max(15, prev - 2));
              } else if (e.key === 'ArrowDown') {
                // Increase table height (make larger) - allowed above default
                setTableHeightPercentage((prev) => Math.min(70, prev + 2));
              }
            }
          }}
          style={{ height: '8px', minHeight: '8px' }}
        >
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-12 h-1 bg-gray-500 rounded-full group-hover:bg-gray-400" />
          </div>
        </button>
        {/* Table Area */}
        <div
          className="rounded-lg flex flex-col shrink-0 max-w-[calc(100vw-8vw)]"
          style={{ height: `${tableHeightPercentage}%` }}
        >
          <div className="flex-1 overflow-auto relative">
            <table className="w-full text-left text-base text-gray-400">
              <thead className="text-xs uppercase bg-container-700 text-gray-200 sticky top-0 z-30 shadow-sm">
                <tr>
                  <th rowSpan={2} className="p-4 w-16 sticky left-0 z-40 bg-container-700 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.3)] rounded-tl-lg rounded-tr-lg">
                    <input
                      type="checkbox"
                      checked={selectedTableRows.size === tableData.length && tableData.length > 0}
                      onChange={toggleAllRows}
                      className="rounded border-gray-600 bg-container-500 text-primary-600 focus:ring-primary-500"
                    />
                  </th>
                  {(tableHeaders.length > 0 ? tableHeaders : excelData?.headers || []).map((h) => (
                    <React.Fragment key={h}>
                      <th rowSpan={2} className="px-4 py-3 whitespace-nowrap">{h}</th>
                      {h === getValues('endTimeColumn') && (
                        <>
                          <th rowSpan={2} className="px-4 py-3 whitespace-nowrap">Time Offset</th>
                          {visibleParameters.map((param) => {
                            const info = parameters.find((p) => p.parameterName === param);
                            const paramName = info?.shortName || param;
                            const unit = info?.unit || '';
                            return (
                              <th key={`combined-${param}`} colSpan={3} className="px-4 py-2 whitespace-nowrap text-center border-b border-gray-600">
                                {paramName} {unit ? `(${unit})` : ''}
                              </th>
                            );
                          })}
                        </>
                      )}
                    </React.Fragment>
                  ))}
                </tr>
                <tr>
                  {visibleParameters.map((param) => (
                    <React.Fragment key={`sub-${param}`}>
                      <th className="px-3 py-2 whitespace-nowrap text-center text-[10px] text-gray-400 font-normal">- Offset</th>
                      <th className="px-3 py-2 whitespace-nowrap text-center text-[10px] text-gray-400 font-normal">Between</th>
                      <th className="px-3 py-2 whitespace-nowrap text-center text-[10px] text-gray-400 font-normal">+ Offset</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableData.map((row, i) => (
                  <ReportTableRow
                    key={row.originalIndex}
                    row={row}
                    index={i}
                    headers={tableHeaders.length > 0 ? tableHeaders : excelData?.headers || []}
                    selectedTableRows={selectedTableRows}
                    toggleRow={toggleRow}
                    rowBorderColors={ROW_BORDER_COLORS}
                    startTimeColumn={getValues('startTimeColumn')}
                    endTimeColumn={getValues('endTimeColumn')}
                    devices={selectedDevices}
                    timeOffset={getValues('timeOffset')}
                    selectedParameters={visibleParameters}
                    // parameters={parameters}
                    clientThresholdSettings={clientThresholdSettings}
                    userConfigDataV2={userConfigDataV2}
                    deviceType={reportDeviceType}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    </PlanGate>
  );
}
